/**
 * Poll Celery task status via GET /api/get-celery-task-log (Mongo celery-task-logs).
 * Used after upload / sheet generation endpoints return a task_id.
 */
import { handleHttpResponse } from "../../utils/http_response_utils";

// ~1 hour max at 15s intervals (240 × 15s)
const POLL_INTERVAL_MS = 15000;
const MAX_STATUS_POLLS = 240;

/** Like setTimeout, but resolves early when `signal` is aborted (e.g. user leaves the page). */
function abortableSleep(ms, signal) {
  if (!signal) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  if (signal.aborted) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const onTimeout = () => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    };
    const onAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", onAbort);
      resolve();
    };
    const timer = setTimeout(onTimeout, ms);
    signal.addEventListener("abort", onAbort);
  });
}

/** Single status check; `signal` cancels the fetch when the poll is aborted. */
const getCeleryTaskLog = async (taskId, signal) => {
  const accessToken = localStorage.getItem("access-token");
  const query = new URLSearchParams({ task_id: taskId });
  const response = await fetch(`/api/get-celery-task-log?${query}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    ...(signal ? { signal } : {}),
  });
  return handleHttpResponse(response);
};

/**
 * Polls /get-celery-task-log until status is completed, failed, or poll budget exhausted.
 * Pass `signal` from an AbortController and call `abort()` on unmount (or when replacing the
 * poll) so in-flight fetches and waits stop promptly.
 *
 * @param {string} taskId
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ outcome: "completed"|"failed"|"timeout"|"status_error"|"aborted", result: unknown }>}
 */
export async function pollCeleryTaskUntilTerminal(taskId, options = {}) {
  const { signal } = options;

  for (let n = 0; n < MAX_STATUS_POLLS; n += 1) {
    if (signal?.aborted) {
      return { outcome: "aborted", result: null };
    }

    let res;
    try {
      res = await getCeleryTaskLog(taskId, signal);
    } catch (err) {
      // Navigation / new poll aborted this request
      if (signal?.aborted || err?.name === "AbortError") {
        return { outcome: "aborted", result: null };
      }
      throw err;
    }

    // Log not ready yet or transient error — retry after interval
    if (!(res instanceof Response) || !res.ok) {
      await abortableSleep(POLL_INTERVAL_MS, signal);
      continue;
    }

    const body = await res.json();
    const log = body.celery_task_log;
    if (!log) {
      return { outcome: "status_error", result: null };
    }
    if (log.status === "completed") {
      return { outcome: "completed", result: log.result ?? null };
    }
    if (log.status === "failed") {
      return { outcome: "failed", result: log.result ?? null };
    }

    // Still pending — wait and poll again
    await abortableSleep(POLL_INTERVAL_MS, signal);
  }
  return { outcome: "timeout", result: null };
}
